import HtmlString from "../util/HtmlString";
import { decodeEntities } from "../util/Misc";
import {ariareaderviewRender} from "./templates/renders/aria-reader-view";

declare const log: any;

/**
 * Aria reader view that adds and queues new messages
 * to be read by screen readers.
 */
// eslint-disable-next-line object-shorthand
const AriaReaderView: any = {
    tagName: "aria-reader-view",
    initialize(options) {
        initComponent(options);
    },
    renderer : ariareaderviewRender,
    created() {
        this.htmlString = new HtmlString();

        /** @type {boolean} - flag to pause adding new queued messages */
        this.isPaused = false;

        /** @type {domElement} - ref to reader element (contains aria-live) */
        this.readerEl = this.el.querySelector(".aria-reader");

        /** @type {string[]} - strings to be read via aria */
        this.messageQueue = [];

        /** @type {number} - timeoutID of queue processing timer */
        this.messageQueueTimer = undefined;

        /** @type {number[]} - State subscription ids we are registed to */
        this.stateSubscribeIds = [];
        this.subscribeToStateChanges();

        /** @type {string} last message set to be read */
        this.lastMsgAppended = "";
    },

    removed() {
        this.stopReading();
        this.unsubscribeFromStateChanges();
    },

    /**
     * Adds a message to the aria reader queue and starts reading if 1st message
     * @param {String}  msg - message to be read (text or html string)
     */
    addMessage(msg) {
        if (typeof msg === "string") {
            this.messageQueue.push(msg);

            if (this.isPaused) {
                return;
            }

            if (this.messageQueue.length === 1) {
                this.startReading(this.firstInitMsgDelay);
                this.firstInitMsgDelay = 0;
            }
        } else {
            log(`Invalid aria reader text and its type is ${typeof msg}`);
        }
    },

    /**
     * Appends the message to the aria html view wrapped proper element types
     * @param {String} msg - message string
     */
    appendAriaMsgToReader(msg) {
        this.lastMsgAppended = msg;

        const liveEl = document.createElement("div");
 
        this.readerEl.appendChild(liveEl);

        if (this.readerEl.childElementCount > this.maxQueueMsgsOnScreen) {
            this.readerEl.removeChild(this.readerEl.firstChild);
        }

        /*
         * Delay the append of the message, allows the readers to pickup
         * the changes + avoid repeats in some cases.
         */
        setTimeout(() => {
            const el = document.createElement("span");
            el.innerHTML = msg;
            liveEl.appendChild(el);

            /**
             * In mobiles, the aria-live element also gets focused when you
             * swipe. Hence, we will hide this div soon after setting the
             * msg. Even after you hide the div, the voice-over/talk-back
             * will continue to read the msg.
             */
            setTimeout(() => {
                liveEl.style.display = "none";
            }, 1000);
        }, 250);
    },

    /**
     * Formats messages to remove all unnecessary items from the message.
     *
     * @param {string} msg - message string to format
     * @return {string} - formated string
     */
    formatMsg(msg) {
        if (this.htmlString.isEmptyString(msg)) {
            return msg;
        }

        let msgFormated = this.htmlString.removeGivenTag(
            decodeEntities(msg), "a", "link"
        );
        msgFormated = this.htmlString.removeHTMLMarkup(msgFormated);
        msgFormated = this.htmlString.removeIdAttributes(msgFormated);
        return msgFormated;
    },

    /**
     * Adds messages from the queue to the aria reader and sets
     * the appropriate time to wait before getting the next message.
     */
    addAriaMsgFromQueue() {
        if (this.messageQueue.length > 0) {
            const msg = this.formatMsg(this.messageQueue.shift());
            this.appendAriaMsgToReader(msg);
            this.startReading(0);
        } else {
            this.stopReading();
        }
    },

    /**
     * Starts reading by processing what is in queue
     * @param {integer} delay - delay in miliseconds before starting.
     */
    startReading(delay) {
        if (this.messageQueueTimer) {
            return;
        }

        /**
         * Add delay to wait time if delay > 0
         * to compensate for append to dom delay.
         */
        this.messageQueueTimer = setTimeout(() => {
            this.stopReading();
            this.addAriaMsgFromQueue();
        }, (delay > 0) ? delay + 250 : delay);
    },

    /**
     * Stop reading by clearing the timer id.
     */
    stopReading() {
        if (this.messageQueueTimer) {
            clearTimeout(this.messageQueueTimer);
            this.messageQueueTimer = undefined;
        }
    },

    /**
     * Pauses the reader from adding new elements to the view
     * but still keeps messages in queue.
     * @param {boolean} state - true to pause false to unpause
     */
    pauseReader(state) {
        this.isPaused = state;

        if (this.isPaused) {
            this.stopReading();
        } else {
            this.startReading(this.firstInitMsgDelay);
        }
    },

    /**
     * Subscribe to app state changes and apply required changes
     */
    subscribeToStateChanges() {
        /**
         * On state change, survey event "ended" and not visible,
         * reset focus to input.
         */
        this.stateSubscribeIds.push(
            this.$app.getStateMgr().subscribe(isMinimized => {
                if (isMinimized) {
                    this.pauseReader(true);
                } else {
                    this.pauseReader(false);
                }
            }, "isMinimized")
        );

        this.stateSubscribeIds.push(
            this.$app.getStateMgr().subscribe(ariaReaderSay => {
                this.addMessage(ariaReaderSay);
            }, "ariaReaderSay")
        );
    },

    /**
     * Unsubscribe from all state callback ids
     */
    unsubscribeFromStateChanges() {
        this.stateSubscribeIds.forEach(subIdx => {
            this.$app.getStateMgr().unsubscribe(subIdx);
        });
        this.stateSubscribeIds.length = 0;
    },
};

/**
 * @export
 * @param {CIApp} appInstance - main app instance
 * @return {Object} AriaReaderView
 */
export default appInstance => {
    AriaReaderView.$app = appInstance;
    return AriaReaderView;
};

const initComponent = (options: any) => {
    const appInstance = options.$app;

    options.constants = {

        /**
         * @type {number} - Wait period on first aria msg set, it is set to
         * zero after 1st call (used as flag). Some readers do not read the
         * 1st message if the element is added to dom and a new value is
         * set to quickly.
         */
        firstInitMsgDelay: 3000,

        /**
         * @type {number} - Max number of messages appended to the view
         * before the oldest is removed. FIFO.
         */
        maxQueueMsgsOnScreen: 30,
    };

    // override defaults with existing appConfigProps.ariaReader if any
    const appConfigProps = appInstance.getUIConfig().appConfigProps || {};

    if (appConfigProps.ariaReader) {
        Object.keys(options.constants).forEach(prop => {
            if (appConfigProps.ariaReader[prop]) {
                options.constants[prop] = appConfigProps.ariaReader[prop];
            }
        });
    }
};
